import joi from 'joi';
import type { CiscoMac } from '../types';

export const pathlookupSetupTypes = [
  'aws',
  'capwap',
  'esp',
  'ethernet',
  'ethernetVlan',
  'fp',
  'gre',
  'ip',
  'ipv6',
  'meraki-vpn',
  'mpls',
  'spbmBackbone', // SPBM (802.1ah) backbone MAC-in-MAC header; carried as an ethernet header, see isPacketHeaderSpbmBackbone
  'tcp',
  'udp',
  'vcmp',
  'vxlan',
  'loop',
] as const;
export type PathLookupSetupType = (typeof pathlookupSetupTypes)[number];
export const headerTypes = [
  'aws',
  'azure',
  'capwap',
  'esp',
  'ethernet',
  'fp',
  'gre',
  'icmp',
  'icmpv6',
  'ip',
  'ipv6',
  'meraki-vpn',
  'mpls',
  'tcp',
  'udp',
  'vcmp',
  'vxlan',
] as const;
export type HeaderType = (typeof headerTypes)[number];

export type PacketHeaderEthernet = {
  dst: CiscoMac | null; // can be null in case of cheating and during cloud routing
  etherType: HeaderType; // this is probably too open, should be IP / IPv6 only?
  src: CiscoMac | null; // when traffic starts on given machine, the src is added later on by the outgoing network interface
  type: 'ethernet';
  vlan?: number;
  iSid?: number; // SPBM (802.1ah) service identifier carried in the I-TAG; set only on a backbone MAC-in-MAC header
};
export function isPacketHeaderEthernet(
  header?: PacketHeader,
): header is PacketHeaderEthernet {
  return header?.type === 'ethernet';
}

export type PacketHeaderEthernetVlan = PacketHeaderEthernet;
export function isPacketHeaderEthernetVlan(
  header?: PacketHeader,
): header is PacketHeaderEthernetVlan {
  return header?.type === 'ethernet' && typeof header.vlan === 'number';
}

/**
 * SPBM / Provider Backbone Bridging (IEEE 802.1ah) backbone header. It is an
 * ethernet header at runtime — a MAC-in-MAC frame is forwarded and popped like
 * ethernet — so it deliberately keeps `type: 'ethernet'` and all the generic
 * ethernet machinery applies. This narrowed variant, distinguished by the
 * presence of `iSid` (mirroring `PacketHeaderEthernetVlan`), lets code and the
 * UI recognise the backbone header: on it `dst`/`src` are B-MACs, `vlan` is the
 * B-VLAN, and `iSid` is the service identifier carried in the I-TAG.
 */
export type PacketHeaderSpbmBackbone = PacketHeaderEthernet & { iSid: number };
export function isPacketHeaderSpbmBackbone(
  header?: PacketHeader,
): header is PacketHeaderSpbmBackbone {
  return header?.type === 'ethernet' && typeof header.iSid === 'number';
}

export type PacketHeaderMpls = {
  stack: number[];
  type: 'mpls';
};
export function isPacketHeaderMpls(
  header?: PacketHeader,
): header is PacketHeaderMpls {
  return header?.type === 'mpls';
}

export const pathLookupIpProtocols = ['icmp', 'icmpv6', 'tcp', 'udp'] as const;
export type PathLookupIpProtocol = (typeof pathLookupIpProtocols)[number];
export const pathLookupIpProtocolSchema = joi
  .string()
  .valid(...pathLookupIpProtocols);

type NonConfigurableIpProtocol = 'esp' | 'gre' | '6in4';

export type PacketHeaderIp = {
  dst: string[];
  'fragment offset': number;
  protocol: PathLookupIpProtocol | NonConfigurableIpProtocol;
  src: string[];
  type: 'ip';
  ttl: number;
};
export function isPacketHeaderIp(
  header?: PacketHeader,
): header is PacketHeaderIp {
  return header?.type === 'ip';
}

export type PacketHeaderIpv6 = {
  dst: string[];
  protocol: PathLookupIpProtocol | NonConfigurableIpProtocol;
  src: string[];
  type: 'ipv6';
  hopLimit: number; // TTL
};
export function isPacketHeaderIpv6(
  header?: PacketHeader,
): header is PacketHeaderIpv6 {
  return header?.type === 'ipv6';
}

export type PacketHeaderIcmpBase = {
  icmpCode: number;
  icmpType: number;
};
export type PacketHeaderIcmp = PacketHeaderIcmpBase & {
  type: 'icmp';
};
export type PacketHeaderIcmpv6 = PacketHeaderIcmpBase & {
  type: 'icmpv6';
};
export function isPacketHeaderIcmp(
  header?: PacketHeader,
): header is PacketHeaderIcmp {
  return header?.type === 'icmp';
}

export const pathLookupTcpFlags = [
  'ack',
  'fin',
  'psh',
  'rst',
  'syn',
  'urg',
] as const;
export type PathLookupTcpFlag = (typeof pathLookupTcpFlags)[number];
export const pathLookupTcpFlagSchema = joi
  .string()
  .valid(...pathLookupTcpFlags);

export type PacketHeaderTcp = {
  dst: string[];
  flags: PathLookupTcpFlag[];
  src: string[];
  type: 'tcp';
};
export function isPacketHeaderTcp(
  header?: PacketHeader,
): header is PacketHeaderTcp {
  return header?.type === 'tcp';
}

export type PacketHeaderUdp = {
  dst: string[];
  src: string[];
  type: 'udp';
};
export function isPacketHeaderUdp(
  header?: PacketHeader,
): header is PacketHeaderUdp {
  return header?.type === 'udp';
}

export type PacketHeaderAws = {
  proto: string;
  vrfName: string;
  type: 'aws';
};
export function isPacketHeaderAws(
  header?: PacketHeader,
): header is PacketHeaderAws {
  return header?.type === 'aws';
}

export type PacketHeaderAzure = {
  proto: string;
  vrfName: string;
  type: 'azure';
};
export function isPacketHeaderAzure(
  header?: PacketHeader,
): header is PacketHeaderAzure {
  return header?.type === 'azure';
}

export type PacketHeaderVcmp = {
  proto: string;
  vrfName: string;
  type: 'vcmp';
};
export function isPacketHeaderVcmp(
  header?: PacketHeader,
): header is PacketHeaderVcmp {
  return header?.type === 'vcmp';
}

export type PacketHeaderVxlan = {
  groupPolicyId?: bigint; // https://datatracker.ietf.org/doc/html/draft-smith-vxlan-group-policy
  vni: number;
  policyApplied: boolean;
  type: 'vxlan';
};
export function isPacketHeaderVxlan(
  header?: PacketHeader,
): header is PacketHeaderVxlan {
  return header?.type === 'vxlan';
}

export type PacketHeaderMerakiVpn = {
  proto: string;
  vrfName: string;
  type: 'meraki-vpn';
};
export function isPacketHeaderMerakiVpn(
  header?: PacketHeader,
): header is PacketHeaderMerakiVpn {
  return header?.type === 'meraki-vpn';
}

export type PacketHeaderCapwap = {
  // no fields supported so far, for the list see: https://datatracker.ietf.org/doc/html/rfc5415#section-4.3
  type: 'capwap';
};
export function isPacketHeaderCapwap(
  header?: PacketHeader,
): header is PacketHeaderCapwap {
  return header?.type === 'capwap';
}

export type PacketHeaderGre = {
  protoType: HeaderType; // this is probably too open, should be IP / IPv6 only?
  type: 'gre';
};
export function isPacketHeaderGre(
  header?: PacketHeader,
): header is PacketHeaderGre {
  return header?.type === 'gre';
}

export type PacketHeaderEsp = {
  nextHeader: HeaderType;
  payload: string;
  type: 'esp';
};
export function isPacketHeaderEsp(
  header?: PacketHeader,
): header is PacketHeaderEsp {
  return header?.type === 'esp';
}

export type PacketHeaderFp = {
  dstSwitchId: number;
  dstSubswitchId: number;
  etherType: 'ethernet';
  srcSwitchId: number;
  ttl: number;
  type: 'fp';
};
export function isPacketHeaderFp(
  header?: PacketHeader,
): header is PacketHeaderFp {
  return header?.type === 'fp';
}

export type PacketHeader =
  | PacketHeaderAws
  | PacketHeaderAzure
  | PacketHeaderCapwap
  | PacketHeaderEsp
  | PacketHeaderEthernet
  | PacketHeaderEthernetVlan
  | PacketHeaderFp
  | PacketHeaderGre
  | PacketHeaderIcmp
  | PacketHeaderIcmpv6
  | PacketHeaderIp
  | PacketHeaderIpv6
  | PacketHeaderMerakiVpn
  | PacketHeaderMpls
  | PacketHeaderTcp
  | PacketHeaderUdp
  | PacketHeaderVcmp
  | PacketHeaderVxlan;

export const SD_WAN_PROTOCOL = [
  'core-network-segment',
  'core-network-function-group',
  'meraki-vpn',
  'vcmp',
  'vwan',
] as const;

export type SD_WAN_PROTOCOL = (typeof SD_WAN_PROTOCOL)[number];
